import { Icon, getByExecutionTypeName } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { memo, useEffect, useRef, useState } from "react";
import { Handle, Position } from "reactflow";
import AbstractStandardNode from "../../customNodeTypes/AbstractStandardNode";
import { Direction } from "../../visualization.service";

const ENDED_STATUSES = new Set([
    "ENDED_SUCCESS",
    "ENDED_ERROR",
    "ENDED_CANCELLED",
]);

const NodeEstimate = memo(
    ({ estimateP75Seconds, startedAt }: { estimateP75Seconds: number; startedAt: string }) => {
        const [elapsed, setElapsed] = useState(0);
        const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

        useEffect(() => {
            if (!startedAt) return;
            const tick = () => setElapsed((Date.now() - Date.parse(startedAt)) / 1000);
            tick();
            intervalRef.current = setInterval(tick, 1000);
            return () => {
                if (intervalRef.current !== null) clearInterval(intervalRef.current);
            };
        }, [startedAt]);

        const progress = Math.min((elapsed / estimateP75Seconds) * 100, 100);
        const isOverrunning = elapsed > estimateP75Seconds;

        return (
            <div
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: "#d0d0d0",
                    borderBottomLeftRadius: 4,
                    borderBottomRightRadius: 4,
                    overflow: "hidden",
                    zIndex: 1,
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${progress}%`,
                        background: isOverrunning ? "#e87040" : "#3bb6fd",
                        transition: "width 1s linear",
                    }}
                />
            </div>
        );
    }
);

export default memo(({ id, data, isConnectable, type, selected }: any) => {
    let selectionClass = "selectionUndefined";
    if (data.selected === true) selectionClass = "selected";
    if (data.selected === false) selectionClass = "unselected";

    const isInFlight = !ENDED_STATUSES.has(data.status);
    const showEstimate =
        isInFlight && data.estimate_p75_seconds != null && data.started_at != null;

    return (
        <>
            <AbstractStandardNode
                label={data.name}
                type={type}
                icon={getByExecutionTypeName(type)}
                className={`${data.status}Status ${data.isParentNode ? "parent" : ""} ${
                    data.isCurrentExecution ? "currentExecution" : ""
                } ${data.is_deleted ? "trashed" : ""} ${
                    data.fading_out ? "fadingOut" : ""
                } ${selectionClass}`}
            >
                <Handle
                    type="source"
                    position={
                        data.layoutDirection === Direction.Horizontal ? Position.Left : Position.Top
                    }
                    isConnectable={isConnectable}
                />
                <Handle
                    type="target"
                    position={
                        data.layoutDirection === Direction.Horizontal
                            ? Position.Right
                            : Position.Bottom
                    }
                    isConnectable={isConnectable}
                />
                <Handle
                    type="source"
                    id="returnee"
                    position={Position.Top}
                    isConnectable={isConnectable}
                    style={{ left: (data.width ?? 30) - 15 }}
                />
                {data.isCurrentExecution ? (
                    <Icon icon={IconCollection.mapMarker} className="positionMarker" />
                ) : null}
                {showEstimate && (
                    <NodeEstimate
                        estimateP75Seconds={data.estimate_p75_seconds}
                        startedAt={data.started_at}
                    />
                )}
            </AbstractStandardNode>
        </>
    );
});
